import React, { useEffect, useState } from 'react';
import InboxIcon from '@mui/icons-material/Inbox';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';
import { LinearProgress, Tooltip } from '@mui/material';
import {
  ButtonIconStyled,
  DesktopNavBar,
  ImageStyled,
  MobileNavBar,
  TitleStyled,
  ToolBarStyled
} from './navBarStyles';
import {
  selectIsLoadingCall,
  selectIsSideBarOpen,
  setOpenSideBar
} from '../../../store/slices/app/app';
import { LocationContextItem } from '../../locationContexItem/locationContexItem';
import { colors } from '../../../constants/colors';
import { ProfileIconAndMenu } from './ProfileMenu/ProfileMenu';
import {
  currentLocation,
  setCurrentLocation
} from '../../../store/slices/locations/location';
import SearchButton from '../../commons/SearchButton/SearchButton';
import Strings from '../../../i18n';
import { GlobalSearch } from '../../commons/GlobalSearch/globalSearch';
import {
  selectCurrentUser,
  setCredentials
} from '../../../store/slices/login/login';
import { systemModuleEnum } from '../../../constants/systemRolesEnums';

const NavBarLogo = require('../../../assets/navBar-logo.png');
const iconLocation = require('../../../assets/location.png');

export const NavBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const roleAdmin = 'Admin';
  const roleServerAccessFull = 'server access full';
  const allowedGlobalSearchModules: systemModuleEnum[] = [
    systemModuleEnum.devices,
    systemModuleEnum.users,
    systemModuleEnum.locations,
    systemModuleEnum.organizations,
    systemModuleEnum.versions,
    systemModuleEnum.reports,
    systemModuleEnum.serviceHealth,
    systemModuleEnum.webJobs,
    systemModuleEnum.deviceAnnounce,
    systemModuleEnum.firmwareSchedule
  ];
  const isOpenSideBar = useSelector(selectIsSideBarOpen) as boolean;
  const contextUser = useSelector(selectCurrentUser);
  const match = useMatch('/locations/:locationId/*');

  const location = useSelector(currentLocation);

  const isLoading = useSelector(selectIsLoadingCall) as boolean;
  const canUseGlobalSearch =
    contextUser?.roleInfo?.systemRoleName === roleAdmin ||
    contextUser?.roleInfo?.permissionsByModule?.some((module) =>
      allowedGlobalSearchModules.includes(
        module.systemModuleName as systemModuleEnum
      )
    );
  const canAccessFullServer =
    contextUser?.roleInfo?.systemRoleName === roleAdmin;

  const setLocationToNull = () => {
    dispatch(setCurrentLocation(null));
    navigate('/locations');
  };

  const handleLogOut = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    dispatch(
      setCredentials({
        user: null,
        email: '',
        token: '',
        picture: '',
        roleInfo: null,
        userInfo: null,
        refreshToken: '',
        authWithApi: false,
        userId: 0
      })
    );
    logout({
      logoutParams: { returnTo: `${window.location.origin}/callback` }
    });
  };

  const redirectToServer = () => {
    dispatch(setCurrentLocation(null));
    navigate('/server-access');
  };

  useEffect(() => {
    if (!match) {
      dispatch(setCurrentLocation(null));
    }
  }, [match]);

  useEffect(() => {
    if (location?.locationId) {
      dispatch(setOpenSideBar(true));
    } else {
      dispatch(setOpenSideBar(false));
    }
  }, [location]);

  return (
    <>
      <DesktopNavBar position="fixed">
        <ToolBarStyled>
          <ImageStyled
            src={NavBarLogo}
            alt="Navbar Logo"
            onClick={() => window.open('/', '_blank')}
          />
          <LocationContextItem
            name={location?.locationName ?? ''}
            canListLocations={canAccessFullServer}
          />

          <TitleStyled onClick={setLocationToNull} />
          <GlobalSearch isEnabled={canUseGlobalSearch} />
          {canAccessFullServer && (
            <Tooltip title={Strings.navBar.userLocationsTooltip}>
              <ButtonIconStyled
                onClick={() => setLocationToNull()}
                className="targetRedirectToLocation"
              >
                <img src={iconLocation} alt="loc" width="15" />
              </ButtonIconStyled>
            </Tooltip>
          )}
          {contextUser &&
            contextUser.roleInfo &&
            contextUser.roleInfo.systemRoleName &&
            contextUser.roleInfo.systemRoleName === roleAdmin && (
              <Tooltip title={Strings.navBar.serverAccessTooltip}>
                <ButtonIconStyled onClick={() => redirectToServer()}>
                  <InsertChartIcon htmlColor={colors.mediumGray} />
                </ButtonIconStyled>
              </Tooltip>
            )}
          {canAccessFullServer && (
            <Tooltip title={Strings.navBar.inboxTooltip}>
              <ButtonIconStyled>
                <InboxIcon htmlColor={colors.mediumGray} />
              </ButtonIconStyled>
            </Tooltip>
          )}
          {canAccessFullServer ? (
            <ProfileIconAndMenu />
          ) : (
            <Tooltip title={Strings.navBar.profileIcon.logOut}>
              <ButtonIconStyled onClick={handleLogOut}>
                <LogoutIcon htmlColor={colors.mediumGray} />
              </ButtonIconStyled>
            </Tooltip>
          )}
        </ToolBarStyled>
        {isLoading && <LinearProgress color="secondary" />}
      </DesktopNavBar>
      <MobileNavBar position="fixed">
        <ToolBarStyled>
          <ImageStyled
            src={NavBarLogo}
            alt="Navbar Logo"
            onClick={() => navigate('/dashboard')}
            onMouseEnter={() => {
              dispatch(setOpenSideBar(!isOpenSideBar));
            }}
          />
          <TitleStyled onClick={setLocationToNull} />
          <GlobalSearch disableShortcuts isEnabled={canUseGlobalSearch} />
          {canAccessFullServer && (
            <ButtonIconStyled onClick={() => setLocationToNull()}>
              <img src={iconLocation} alt="loc" width="15" />
            </ButtonIconStyled>
          )}
          {canAccessFullServer && (
            <ButtonIconStyled onClick={() => redirectToServer()}>
              <InsertChartIcon htmlColor={colors.mediumGray} />
            </ButtonIconStyled>
          )}
          {canAccessFullServer && (
            <ButtonIconStyled>
              <InboxIcon htmlColor={colors.mediumGray} />
            </ButtonIconStyled>
          )}
          {canAccessFullServer ? (
            <ProfileIconAndMenu />
          ) : (
            <ButtonIconStyled onClick={handleLogOut}>
              <LogoutIcon htmlColor={colors.mediumGray} />
            </ButtonIconStyled>
          )}
        </ToolBarStyled>
        {isLoading && <LinearProgress color="secondary" />}
      </MobileNavBar>
    </>
  );
};
